import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import EmptyState from "../../../../shared/components/EmptyState";
import GradientShareButton from "../../../../shared/components/GradientShareButton";
import InfoTooltip from "../../../../shared/components/InfoTooltip";
import LoadingPage from "../../../../shared/components/LoadingPage";
import Toast from "../../../../shared/components/Toast";
import UserAvatar from "../../../../shared/components/UserAvatar";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { buildLoginHref } from "../../../../shared/lib/loginRedirect";
import {
  buildOpenMatchShareUrl,
  syncCanonicalShareUrl,
} from "../../../../shared/lib/shareLinks";
import { useAuth } from "../../../auth/shared/hooks/useAuth";
import { normalizeUserCategory } from "../../../auth/shared/types/auth";
import {
  getClubBySlug,
  listClubs,
} from "../../../clubs/shared/services/clubsService";
import type { Club } from "../../../clubs/shared/types/club";
import {
  addMatchParticipant,
  cancelMatch,
  cancelMyMatchParticipant,
  confirmMatch,
  createMatchMessage,
  deleteMatchMessage,
  getAuthenticatedMatch,
  getPublicMatch,
  listMatchMessages,
  markMatchChatRead,
  requestJoinMatch,
  reviewMatchParticipant,
  updateMatch,
} from "../../shared/services/matchesService";
import type {
  CreateOpenMatchPayload,
  OpenMatchInitialParticipant,
  OpenMatchItem,
  OpenMatchMessage,
} from "../../shared/types/matches";
import {
  buildOpenMatchMapUrl,
  formatOpenMatchCategory,
  formatOpenMatchCurrency,
  formatOpenMatchGenderPreference,
  formatOpenMatchPerPlayerPrice,
  formatOpenMatchPaymentMode,
  formatOpenMatchScheduleRange,
  formatOpenMatchSideRequirement,
  isOpenMatchFinished,
  parseOpenMatchDateTimeValue,
  resolveOpenMatchCategoryClass,
  resolveOpenMatchDisplayTitle,
  resolveOpenMatchDurationMinutes,
} from "../../shared/utils/openMatchPresentation";
import "./MatchesPage.css";
import quadraPadelTransparente from "../../../../assets/images/quadra-padel-transparente.png";
import MatchCreatePanel from "../components/MatchCreatePanel";
import MatchParticipantModal from "../components/MatchParticipantModal";

type MatchDetailPageProps = {
  matchId: number;
};

type MatchPlayerAvatarSex = "male" | "female" | "other" | "";
const SELF_CANCEL_CUTOFF_HOURS = 4;
const COURT_SLOT_COUNT = 4;
const MATCH_CHAT_MESSAGE_LIMIT = 1000;
const ACTIVE_CURRENT_PARTICIPANT_STATUSES = new Set([
  "confirmed",
  "pending",
  "invited",
]);

function normalizeMatchPlayerAvatarSex(
  value?: string | null,
): MatchPlayerAvatarSex {
  if (value === "male" || value === "female" || value === "other") return value;
  return "";
}

function MatchPlayerStatusIcon({
  status,
  label,
}: {
  status: string;
  label: string;
}) {
  if (status === "pending" || status === "invited") {
    return (
      <span
        className="match-player-slot__status match-player-slot__status--pending material-symbols-outlined"
        aria-label={label}
        title={label}
      >
        schedule
      </span>
    );
  }
  if (status === "confirmed") {
    return (
      <span
        className="match-player-slot__status match-player-slot__status--confirmed material-symbols-outlined"
        aria-label={label}
        title={label}
      >
        check_circle
      </span>
    );
  }
  return null;
}

export default function MatchDetailPage({ matchId }: MatchDetailPageProps) {
  const { token, user } = useAuth();
  const texts = siteTexts.openMatches;
  const [match, setMatch] = useState<OpenMatchItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const [cancelParticipantLoading, setCancelParticipantLoading] =
    useState(false);
  const [participantActionId, setParticipantActionId] = useState<number | null>(
    null,
  );
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [addParticipantSaving, setAddParticipantSaving] = useState(false);
  const [editingMatchOpen, setEditingMatchOpen] = useState(false);
  const [editClubs, setEditClubs] = useState<Club[]>([]);
  const [editClubsLoading, setEditClubsLoading] = useState(false);
  const [resolvedClubLogoUrl, setResolvedClubLogoUrl] = useState("");
  const [chatMessages, setChatMessages] = useState<OpenMatchMessage[]>([]);
  const [chatDraft, setChatDraft] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState("");
  const [removingMessageId, setRemovingMessageId] = useState<number | null>(null);

  useEffect(() => {
    syncCanonicalShareUrl(buildOpenMatchShareUrl(matchId));
  }, [matchId]);

  const fetchMatchSnapshot = useCallback(async () => {
    const response = token
      ? await getAuthenticatedMatch(token, matchId).catch(() =>
          getPublicMatch(matchId),
        )
      : await getPublicMatch(matchId);
    return response.match || null;
  }, [matchId, token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    fetchMatchSnapshot()
      .then((nextMatch) => {
        if (!cancelled) setMatch(nextMatch);
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : texts.detail.notFound,
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchMatchSnapshot, texts.detail.notFound]);

  async function refreshMatchSnapshot(fallbackMatch?: OpenMatchItem | null) {
    try {
      const nextMatch = await fetchMatchSnapshot();
      setMatch(nextMatch || fallbackMatch || null);
      return nextMatch;
    } catch (_err) {
      if (fallbackMatch) {
        setMatch(fallbackMatch);
      }
      return fallbackMatch || null;
    }
  }

  useEffect(() => {
    setChatMessages(match?.messages || []);
  }, [match?.messages]);

  useEffect(() => {
    if (!token || !match?.chat?.canAccess) return undefined;
    let cancelled = false;

    const loadChat = async (showLoading = false) => {
      if (document.hidden) return;
      if (showLoading) setChatLoading(true);
      try {
        const response = await listMatchMessages(token, match.id);
        if (cancelled) return;
        setChatMessages(response.messages || []);
        setMatch((current) =>
          current && current.id === match.id
            ? { ...current, chat: response.chat, messages: response.messages }
            : current,
        );
        setChatError("");
      } catch (err) {
        if (!cancelled) {
          setChatError(
            err instanceof Error ? err.message : texts.detail.messageError,
          );
        }
      } finally {
        if (!cancelled && showLoading) setChatLoading(false);
      }
    };

    loadChat(true);
    const intervalId = window.setInterval(() => {
      loadChat(false);
    }, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [match?.chat?.canAccess, match?.id, texts.detail.messageError, token]);

  useEffect(() => {
    if (!editingMatchOpen || editClubs.length > 0) return undefined;
    let cancelled = false;
    setEditClubsLoading(true);
    listClubs({})
      .then((response) => {
        if (!cancelled) setEditClubs(response.clubs || []);
      })
      .catch(() => {
        if (!cancelled) setEditClubs([]);
      })
      .finally(() => {
        if (!cancelled) setEditClubsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editClubs.length, editingMatchOpen]);

  useEffect(() => {
    const logoUrl = String(match?.clubLogoUrl || "").trim();
    if (logoUrl) {
      setResolvedClubLogoUrl(logoUrl);
      return undefined;
    }
    setResolvedClubLogoUrl("");
    const slug = String(match?.clubSlug || "").trim();
    if (!slug) return undefined;
    let cancelled = false;
    getClubBySlug(slug)
      .then((response) => {
        if (!cancelled) {
          setResolvedClubLogoUrl(String(response.club?.logoUrl || "").trim());
        }
      })
      .catch(() => {
        if (!cancelled) setResolvedClubLogoUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [match?.clubLogoUrl, match?.clubSlug]);

  const estimatedValue = useMemo(() => {
    if (!match) return formatOpenMatchCurrency(0);
    return formatOpenMatchPerPlayerPrice(match);
  }, [match]);
  const hasVariablePerPlayerPrice =
    match?.priceMode !== "player" &&
    Number(match?.totalPlayers || 0) > COURT_SLOT_COUNT;

  const detailsText = useMemo(() => {
    if (!match) return "";
    return Array.from(
      new Set(
        [match.description, match.notes, match.paymentInstructions]
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      ),
    ).join("\n\n");
  }, [match]);

  const scheduleLabel = useMemo(
    () => (match ? formatOpenMatchScheduleRange(match) : ""),
    [match],
  );
  const displayTitle = useMemo(
    () => (match ? resolveOpenMatchDisplayTitle(match) : texts.title),
    [match, texts.title],
  );
  const mapUrl = useMemo(
    () => (match ? buildOpenMatchMapUrl(match) : ""),
    [match],
  );
  const clubHref = useMemo(
    () => (match?.clubSlug ? `#/clubes/${match.clubSlug}` : ""),
    [match?.clubSlug],
  );
  const uniqueCategories = useMemo(
    () => Array.from(new Set((match?.categories || []).filter(Boolean))),
    [match?.categories],
  );
  const clubLogoUrl = resolvedClubLogoUrl || String(match?.clubLogoUrl || "");
  const isOrganizer = Boolean(user && match && user.id === match.organizer.id);
  const isMatchCanceled = match?.status === "canceled";
  const isMatchFinished = Boolean(match && !isMatchCanceled && isOpenMatchFinished(match));
  const isMatchConfirmed = Boolean(match?.confirmedAt);
  const confirmedFilledSlots =
    (match?.organizerPlays ? 1 : 0) +
    Number(match?.participantSummary?.confirmed || 0);
  const isMatchFull =
    match?.status === "full" &&
    confirmedFilledSlots >= Number(match?.totalPlayers || 0);
  const activePlayers = useMemo(() => {
    if (!match) return [];
    return [
      ...(match.organizerPlays
        ? [
            {
              id: "organizer",
              name: match.organizer.name,
              avatarUrl: match.organizer.avatarUrl || "",
              sex: "" as MatchPlayerAvatarSex,
              meta: texts.detail.organizerPlayer,
              status: "confirmed",
              participantId: null,
              userId: match.organizer.id,
              isPlatformUser: true,
            },
          ]
        : []),
      ...(match.participants || [])
        .filter((participant) =>
          ["confirmed", "pending", "invited"].includes(participant.status),
        )
        .map((participant) => ({
          id: `participant-${participant.id}`,
          name: participant.participantName || texts.detail.participantFallback,
          avatarUrl: participant.avatarUrl || "",
          sex: normalizeMatchPlayerAvatarSex(participant.sex),
          meta: texts.detail.participantStatuses[participant.status],
          status: participant.status,
          participantId: participant.id,
          userId: participant.userId,
          isPlatformUser: Boolean(participant.userId),
        })),
    ];
  }, [
    match,
    texts.detail.organizerPlayer,
    texts.detail.participantFallback,
    texts.detail.participantStatuses,
  ]);
  const courtPlayers = activePlayers.slice(0, COURT_SLOT_COUNT);
  const reservePlayers = activePlayers.slice(
    COURT_SLOT_COUNT,
    Math.max(COURT_SLOT_COUNT, Number(match?.totalPlayers || 0)),
  );
  const totalConfiguredSlots = Math.max(0, Number(match?.totalPlayers || 0));
  const occupiedSlots = Math.min(activePlayers.length, totalConfiguredSlots);
  const courtOccupiedSlots = Math.min(courtPlayers.length, COURT_SLOT_COUNT);
  const reserveCapacity = Math.max(0, totalConfiguredSlots - COURT_SLOT_COUNT);
  const reserveOccupiedSlots = Math.min(reservePlayers.length, reserveCapacity);
  const reserveAvailableSlots = Math.max(
    0,
    reserveCapacity - reserveOccupiedSlots,
  );
  const canConfirmMatch = Boolean(
    isOrganizer &&
      match &&
      !isMatchCanceled &&
      !isMatchFinished &&
      !isMatchConfirmed,
  );
  const hasCourtSlot =
    occupiedSlots < Math.min(COURT_SLOT_COUNT, totalConfiguredSlots);

  const rawCurrentParticipantStatus = match?.currentUserParticipant?.status || "";
  const currentParticipantStatus = ACTIVE_CURRENT_PARTICIPANT_STATUSES.has(
    rawCurrentParticipantStatus,
  )
    ? rawCurrentParticipantStatus
    : "";
  const canCancelOwnParticipation = Boolean(
    match &&
    !isMatchCanceled &&
    !isMatchFinished &&
    !isOrganizer &&
    currentParticipantStatus === "confirmed" &&
    Number(parseOpenMatchDateTimeValue(match.startAt)?.getTime() || 0) - Date.now() >=
      SELF_CANCEL_CUTOFF_HOURS * 60 * 60 * 1000,
  );
  const userCategory = normalizeUserCategory(user?.category || "");
  const userCategoryAllowed = Boolean(
    match && userCategory && (match.categories || []).includes(userCategory),
  );
  const availableSlots = Math.max(0, totalConfiguredSlots - occupiedSlots);
  const acceptsNewParticipation = Boolean(
    match &&
      !isMatchFinished &&
      !isMatchConfirmed &&
      ["open", "full"].includes(match.status) &&
      availableSlots > 0,
  );
  const canRequestJoin = Boolean(
    match &&
    user &&
    !isOrganizer &&
    !currentParticipantStatus &&
    userCategoryAllowed &&
    acceptsNewParticipation,
  );
  const canAcceptInvite = Boolean(
    match &&
      user &&
      !isOrganizer &&
      !isMatchFinished &&
      currentParticipantStatus === "invited" &&
      ["open", "full"].includes(match.status),
  );
  const canDeclineInvite = Boolean(
    match &&
      user &&
      !isOrganizer &&
      !isMatchCanceled &&
      !isMatchFinished &&
      currentParticipantStatus === "invited",
  );
  const chatState = match?.chat;
  const canAccessChat = Boolean(chatState?.canAccess);
  const canSendChat = Boolean(chatState?.canSend);
  const chatReadOnlyMessage = useMemo(() => {
    if (!chatState?.readOnly) return "";
    const expiresAt = new Date(chatState.expiresAt || "");
    const formatted = Number.isNaN(expiresAt.getTime())
      ? ""
      : new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }).format(expiresAt);
    return texts.detail.messageReadOnly.replace(
      "{date}",
      formatted || texts.card.statusFinished,
    );
  }, [chatState?.expiresAt, chatState?.readOnly, texts.card.statusFinished, texts.detail.messageReadOnly]);
  const joinHref = buildLoginHref(`#/partidas/${matchId}`);
  const courtSlots = Array.from({ length: COURT_SLOT_COUNT }, (_, index) => ({
    id: `court-slot-${index}`,
    player: courtPlayers[index] || null,
  }));
  const reserveSlots = [
    ...reservePlayers.map((player) => ({
      id: player.id,
      player,
    })),
    ...Array.from({ length: reserveAvailableSlots }, (_, index) => ({
      id: `reserve-slot-${index}`,
      player: null,
    })),
  ];
  const summaryFilledSlots = occupiedSlots;
  const courtAvailableSlots = Math.max(
    0,
    Math.min(COURT_SLOT_COUNT, totalConfiguredSlots) - courtOccupiedSlots,
  );
  const joinActionLabel = canAcceptInvite
    ? texts.detail.acceptInvite
    : hasCourtSlot
      ? texts.detail.joinCourtAction
      : availableSlots > 0
        ? texts.detail.joinReserveAction
        : isMatchFull
          ? texts.detail.matchSoldOut
          : texts.card.waitingListClosed;
  const availabilityLabel =
    isMatchCanceled
      ? texts.card.statusCanceled
      : isMatchFinished
        ? texts.card.statusFinished
      : isMatchConfirmed
        ? texts.card.statusConfirmed
      : isMatchFull
      ? texts.detail.matchSoldOut
      : availableSlots <= 0
        ? texts.card.waitingListClosed
      : hasCourtSlot
        ? texts.detail.courtVacancies.replace(
            "{count}",
            String(courtAvailableSlots),
          )
        : texts.detail.reserveVacancies.replace(
            "{count}",
            String(reserveAvailableSlots),
          );
  const reserveHintLabel =
    reserveAvailableSlots > 0
      ? texts.detail.reserveVacancies.replace(
          "{count}",
          String(reserveAvailableSlots),
        )
      : isMatchFull
        ? texts.detail.matchSoldOut
        : texts.card.waitingListClosed;
  const shouldShowUnavailableVacancyNotice = Boolean(
    !isMatchCanceled &&
      !isMatchFinished &&
      !isMatchConfirmed &&
      !acceptsNewParticipation &&
      availableSlots <= 0,
  );
  const summarySegments = Array.from(
    { length: totalConfiguredSlots },
    (_, index) => ({
      id: `summary-segment-${index}`,
      filled: index < summaryFilledSlots,
      court: index < COURT_SLOT_COUNT,
    }),
  );

  async function handleShare() {
    if (isMatchCanceled) return;
    const shareUrl = buildOpenMatchShareUrl(matchId);
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToastMessage(siteTexts.profile.user.dashboard.share.copied);
    } catch (_err) {
      setToastMessage(shareUrl);
    }
  }

  async function handleJoinMatch() {
    if (!token) return;
    setJoinLoading(true);
    try {
      const response = await requestJoinMatch(token, matchId);
      const nextMatch = response.match || match;
      if (nextMatch) setMatch(nextMatch);
      await refreshMatchSnapshot(nextMatch);
      const status =
        response.participant?.status ||
        response.match.currentUserParticipant?.status;
      setToastMessage(
        status === "confirmed"
          ? texts.detail.joinConfirmed
          : texts.detail.joinPending,
      );
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.joinError,
      );
    } finally {
      setJoinLoading(false);
    }
  }

  async function handleCancelMyParticipation() {
    if (!token || (!canCancelOwnParticipation && !canDeclineInvite)) return;
    setCancelParticipantLoading(true);
    try {
      const response = await cancelMyMatchParticipant(token, matchId);
      const nextMatch = response.match || match;
      if (nextMatch) setMatch(nextMatch);
      await refreshMatchSnapshot(nextMatch);
      setToastMessage(
        canDeclineInvite
          ? texts.detail.declineInviteSuccess
          : texts.detail.cancelParticipantSuccess,
      );
    } catch (err) {
      setToastMessage(
        err instanceof Error
          ? err.message
          : texts.detail.cancelParticipantError,
      );
    } finally {
      setCancelParticipantLoading(false);
    }
  }

  async function handleReviewParticipant(
    participantId: number,
    status: "confirmed" | "declined" | "canceled",
  ) {
    if (!token || !isOrganizer || isMatchCanceled) return;
    setParticipantActionId(participantId);
    try {
      const response = await reviewMatchParticipant(
        token,
        matchId,
        participantId,
        status,
      );
      const nextMatch = response.match || match;
      if (nextMatch) setMatch(nextMatch);
      await refreshMatchSnapshot(nextMatch);
      setToastMessage(texts.detail.reviewSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.reviewError,
      );
    } finally {
      setParticipantActionId(null);
    }
  }

  async function handleAddParticipant(
    targetMatch: OpenMatchItem,
    payload: OpenMatchInitialParticipant,
  ) {
    if (!token || !isOrganizer || isMatchCanceled) return;
    setAddParticipantSaving(true);
    try {
      const response = await addMatchParticipant(token, targetMatch.id, payload);
      const nextMatch = response.match || match;
      if (nextMatch) setMatch(nextMatch);
      await refreshMatchSnapshot(nextMatch);
      setAddParticipantOpen(false);
      setToastMessage(texts.detail.addParticipantSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.addParticipantError,
      );
    } finally {
      setAddParticipantSaving(false);
    }
  }

  async function handleUpdateMatch(payload: CreateOpenMatchPayload) {
    if (!token || !match || !isOrganizer || isMatchCanceled) {
      throw new Error(texts.actions.createLogin);
    }
    const response = await updateMatch(token, match.id, payload);
    const nextMatch = response.match || match;
    if (nextMatch) setMatch(nextMatch);
    await refreshMatchSnapshot(nextMatch);
    setEditingMatchOpen(false);
    setToastMessage(siteTexts.profile.openMatches.updateSuccess);
  }

  async function handleCancelMatch() {
    if (!token || !match || !isOrganizer || isMatchCanceled || isMatchFinished) {
      throw new Error(texts.actions.createLogin);
    }
    if (!window.confirm(texts.form.cancelMatchConfirm)) return;
    const response = await cancelMatch(token, match.id);
    const nextMatch = response.match || match;
    if (nextMatch) setMatch(nextMatch);
    await refreshMatchSnapshot(nextMatch);
    setEditingMatchOpen(false);
    setToastMessage(texts.form.cancelMatchSuccess);
  }

  async function handleConfirmMatch() {
    if (!token || !match || !isOrganizer || isMatchCanceled) return;
    setParticipantActionId(match.id);
    try {
      const response = await confirmMatch(token, match.id);
      const nextMatch = response.match || match;
      if (nextMatch) setMatch(nextMatch);
      await refreshMatchSnapshot(nextMatch);
      setToastMessage(texts.detail.confirmMatchSuccess);
    } catch (err) {
      setToastMessage(
        err instanceof Error ? err.message : texts.detail.confirmMatchError,
      );
    } finally {
      setParticipantActionId(null);
    }
  }

  async function handleSendChatMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !match || !canSendChat) return;
    const message = chatDraft.trim();
    if (!message) return;
    setChatSending(true);
    try {
      const response = await createMatchMessage(token, match.id, message);
      setChatMessages(response.messages || []);
      setMatch({ ...match, chat: response.chat, messages: response.messages });
      setChatDraft("");
      setChatError("");
    } catch (err) {
      setChatError(err instanceof Error ? err.message : texts.detail.messageError);
    } finally {
      setChatSending(false);
    }
  }

  async function handleMarkChatRead() {
    if (!token || !match || !canAccessChat) return;
    try {
      const response = await markMatchChatRead(token, match.id);
      setChatMessages(response.messages || []);
      setMatch({ ...match, chat: response.chat, messages: response.messages });
    } catch (_err) {
      // Reading state is a convenience; keep the conversation visible if it fails.
    }
  }

  async function handleRemoveChatMessage(messageId: number) {
    if (!token || !match) return;
    setRemovingMessageId(messageId);
    try {
      const response = await deleteMatchMessage(token, match.id, messageId);
      setChatMessages(response.messages || []);
      setMatch({ ...match, chat: response.chat, messages: response.messages });
    } catch (err) {
      setChatError(err instanceof Error ? err.message : texts.detail.messageError);
    } finally {
      setRemovingMessageId(null);
    }
  }

  if (loading) return <LoadingPage label={siteTexts.common.loading} />;

  if (!match || loadError) {
    return (
      <AppLayout title={texts.title}>
        <EmptyState title={texts.detail.notFound} description={loadError} />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={<span className="matches-page-title">{displayTitle}</span>}
      subtitle={texts.subtitle}
    >
      <div className="">
        <section className="match-detail-card match-detail-card--structured">
          <header className="match-detail-hero">
            <div className="match-detail-hero__copy">
              <span className="match-detail-hero__availability">
                <span className="material-symbols-outlined" aria-hidden="true">
                  groups
                </span>
                {availabilityLabel}
              </span>
              <div className="match-detail-hero__meta">
                <span>
                  <span
                    className="material-symbols-outlined"
                    aria-hidden="true"
                  >
                    event
                  </span>
                  {scheduleLabel}
                </span>
                <span>
                  {clubLogoUrl ? (
                    <img
                      className="match-detail-hero__club-logo"
                      src={clubLogoUrl}
                      alt=""
                      aria-hidden="true"
                    />
                  ) : (
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      location_on
                    </span>
                  )}
                  {match.clubName}
                  {match.city ? ` • ${match.city}` : ""}
                  {match.state ? `, ${match.state}` : ""}
                </span>
              </div>
            </div>
            <div className="match-detail-hero__organizer">
              <UserAvatar
                name={match.organizer.name}
                avatarUrl={match.organizer.avatarUrl || undefined}
                size={58}
              />
              <div>
                <span>{texts.card.organizer}</span>
                <strong>{match.organizer.name}</strong>
              </div>
            </div>
            <div className="match-detail-hero__actions">
              {!isMatchCanceled ? (
                <>
                  {mapUrl ? (
                    <a
                      className="ghost-button"
                      href={mapUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        map
                      </span>
                      {texts.actions.openMap}
                    </a>
                  ) : null}
                  <GradientShareButton onClick={() => void handleShare()}>
                    {texts.actions.share}
                  </GradientShareButton>
                  {isOrganizer ? (
                    <>
                      {!isMatchFinished && !isMatchConfirmed ? (
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => setEditingMatchOpen(true)}
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            edit
                          </span>
                          {texts.actions.edit}
                        </button>
                      ) : null}
                      {!isMatchFinished && !isMatchConfirmed ? (
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => void handleConfirmMatch()}
                          disabled={
                            !canConfirmMatch ||
                            participantActionId === match.id
                          }
                          title={
                            canConfirmMatch
                              ? undefined
                              : texts.detail.confirmMatchBlocked
                          }
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            event_available
                          </span>
                          {participantActionId === match.id
                            ? texts.detail.confirmMatchLoading
                            : texts.detail.confirmMatch}
                        </button>
                      ) : null}
                      {!isMatchFinished ? (
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => void handleCancelMatch()}
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            event_busy
                          </span>
                          {texts.form.cancelMatch}
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          </header>

          {isMatchCanceled ? (
            <div
              className="match-detail-canceled-alert"
              role="status"
              aria-live="polite"
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                event_busy
              </span>
              <div>
                <strong>{texts.detail.canceledAlertTitle}</strong>
                <p>{texts.detail.canceledAlertDescription}</p>
              </div>
            </div>
          ) : isMatchFinished ? (
            <div
              className="match-detail-confirmed-alert match-detail-finished-alert"
              role="status"
              aria-live="polite"
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                event_available
              </span>
              <div>
                <strong>{texts.card.statusFinished}</strong>
                <p>{texts.detail.finishedMatchNotice}</p>
              </div>
            </div>
          ) : isMatchConfirmed ? (
            <div
              className="match-detail-confirmed-alert"
              role="status"
              aria-live="polite"
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                event_available
              </span>
              <div>
                <strong>{texts.card.statusConfirmed}</strong>
                <p>{texts.detail.confirmedMatchNotice}</p>
              </div>
            </div>
          ) : null}

          <div className="match-detail-layout">
            <section className="match-detail-panel match-detail-summary">
              <h3 className="match-detail-panel__title">
                <span className="material-symbols-outlined" aria-hidden="true">
                  groups
                </span>
                {texts.detail.summaryTitle}
              </h3>
              <strong className="match-detail-summary__count">
                {texts.detail.participantsSummary
                  .replace("{filled}", String(summaryFilledSlots))
                  .replace("{total}", String(totalConfiguredSlots))}
              </strong>
              <div
                className="match-detail-summary__progress"
                aria-hidden="true"
              >
                {summarySegments.map((segment) => (
                  <span
                    key={segment.id}
                    className={`match-detail-summary__segment${
                      segment.filled ? " is-filled" : ""
                    }${segment.court ? " is-court" : " is-reserve"}`}
                  />
                ))}
              </div>
              <div className="match-detail-summary__legend">
                <span>
                  <i aria-hidden="true" />
                  {texts.detail.onCourtSummary.replace(
                    "{count}",
                    String(courtOccupiedSlots),
                  )}
                </span>
                <span>
                  <i aria-hidden="true" />
                  {texts.detail.reserveSummary.replace(
                    "{count}",
                    String(reserveOccupiedSlots),
                  )}
                </span>
              </div>

              <div className="match-detail-summary__price">
                <small>{siteTexts.eventDetails.amountPerPlayer}</small>
                <span className="match-detail-summary__price-value">
                  <strong>{estimatedValue}</strong>
                  {hasVariablePerPlayerPrice ? (
                    <InfoTooltip
                      ariaLabel={texts.card.priceInfoAriaLabel}
                      content={texts.card.priceInfo}
                    />
                  ) : null}
                </span>
                <span>{texts.card.estimatedPerPlayer}</span>
              </div>

              <div className="match-detail-summary__notice" role="note">
                <span className="material-symbols-outlined" aria-hidden="true">
                  verified_user
                </span>
                <p>{texts.detail.organizerNotice}</p>
              </div>

              <div className="match-detail-summary__actions">
                {!user && availableSlots > 0 ? (
                  <a className="primary-button" href={joinHref}>
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      person_add
                    </span>
                    {joinActionLabel}
                  </a>
                ) : canRequestJoin || canAcceptInvite ? (
                  <>
                    <button
                      type="button"
                      className="primary-button"
                      onClick={() => void handleJoinMatch()}
                      disabled={joinLoading}
                    >
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        person_add
                      </span>
                      {joinLoading ? texts.detail.joinLoading : joinActionLabel}
                    </button>
                    {canDeclineInvite ? (
                      <button
                        type="button"
                        className="ghost-button"
                        onClick={() => void handleCancelMyParticipation()}
                        disabled={cancelParticipantLoading}
                      >
                        {cancelParticipantLoading
                          ? texts.detail.cancelParticipantLoading
                          : texts.detail.declineInvite}
                      </button>
                    ) : null}
                  </>
                ) : !user ? (
                  <button type="button" className="primary-button" disabled>
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      block
                    </span>
                    {joinActionLabel}
                  </button>
                ) : !isOrganizer &&
                  !currentParticipantStatus &&
                  !userCategoryAllowed &&
                  acceptsNewParticipation ? (
                  <span className="match-detail-card__participant-status">
                    {texts.detail.joinCategoryBlocked}
                  </span>
                ) : !isOrganizer &&
                  !currentParticipantStatus &&
                  availableSlots <= 0 ? (
                  <button type="button" className="primary-button" disabled>
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      block
                    </span>
                    {joinActionLabel}
                  </button>
                ) : currentParticipantStatus ? (
                  <>
                    <span className="match-detail-card__participant-status">
                      {
                        texts.detail.participantStatuses[
                          currentParticipantStatus
                        ]
                      }
                    </span>
                    {!isOrganizer &&
                    !isMatchCanceled &&
                    currentParticipantStatus === "confirmed" ? (
                      <button
                        type="button"
                        className="ghost-button"
                        onClick={() => void handleCancelMyParticipation()}
                        disabled={
                          !canCancelOwnParticipation || cancelParticipantLoading
                        }
                        title={
                          canCancelOwnParticipation
                            ? undefined
                            : texts.detail.cancelParticipantSelfBlocked
                        }
                      >
                        {cancelParticipantLoading
                          ? texts.detail.cancelParticipantLoading
                          : texts.detail.cancelParticipantSelf}
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>

              {shouldShowUnavailableVacancyNotice && (
                <div className="match-detail-summary__vacancy">
                  <i aria-hidden="true" />
                  {reserveHintLabel}
                </div>
              )}
            </section>
            <aside
              className="match-detail-side"
              aria-label={texts.detail.summaryTitle}
            >
              <div className="match-detail-main">
                <section className="match-detail-panel match-detail-panel--court">
                  <h3 className="match-detail-panel__title">
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      groups
                    </span>
                    {texts.detail.courtPlayersTitle}
                  </h3>
                  <div
                    className="match-court"
                    aria-label={texts.detail.courtPlayersTitle}
                  >
                    <img
                      className="match-court-background"
                      src={quadraPadelTransparente}
                      alt=""
                    />
                    <div className="match-court__slots">
                      {courtSlots.map(({ id, player }) => (
                        <article
                          key={id}
                          className={`match-player-slot match-player-slot--court${
                            player ? "" : " match-player-slot--empty"
                          }${
                            player && !player.isPlatformUser
                              ? " match-player-slot--external"
                              : ""
                          }${
                            !isMatchCanceled &&
                            !isMatchFinished &&
                            !isMatchConfirmed &&
                            !player &&
                            isOrganizer
                              ? " match-player-slot--clickable"
                              : ""
                          }${
                            !isMatchCanceled && player?.userId
                              ? " match-player-slot--profile-link"
                              : ""
                          }`}
                          onClick={
                            !isMatchCanceled &&
                            !isMatchFinished &&
                            !isMatchConfirmed &&
                            !player &&
                            isOrganizer
                              ? () => setAddParticipantOpen(true)
                              : !isMatchCanceled && player?.userId
                                ? () => {
                                    window.location.hash = `#/jogadores/${player.userId}`;
                                  }
                              : undefined
                          }
                          onKeyDown={
                            !isMatchCanceled &&
                            !isMatchFinished &&
                            !isMatchConfirmed &&
                            !player &&
                            isOrganizer
                              ? (event) => {
                                  if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                  ) {
                                    event.preventDefault();
                                    setAddParticipantOpen(true);
                                  }
                                }
                              : !isMatchCanceled && player?.userId
                                ? (event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();
                                      window.location.hash = `#/jogadores/${player.userId}`;
                                    }
                                  }
                              : undefined
                          }
                          role={
                            !isMatchCanceled &&
                            !isMatchFinished &&
                            !isMatchConfirmed &&
                            !player &&
                            isOrganizer
                              ? "button"
                              : !isMatchCanceled && player?.userId
                                ? "link"
                                : undefined
                          }
                          tabIndex={
                            (!isMatchCanceled &&
                              !isMatchFinished &&
                              !isMatchConfirmed &&
                              !player &&
                              isOrganizer) ||
                            (!isMatchCanceled && player?.userId)
                              ? 0
                              : undefined
                          }
                        >
                          {player ? (
                            <>
                              <UserAvatar
                                name={player.name}
                                avatarUrl={player.avatarUrl || undefined}
                                sex={player.sex || undefined}
                                size={50}
                              />
                              <div>
                                <div className="match-player-slot__identity">
                                  <strong>{player.name}</strong>
                                  <MatchPlayerStatusIcon
                                    status={player.status}
                                    label={player.meta}
                                  />
                                </div>
                                {!player.isPlatformUser ? (
                                  <span className="match-player-slot__platform-badge">
                                    {texts.detail.participantNotRegistered}
                                  </span>
                                ) : null}
                                {!isMatchCanceled &&
                                !isMatchFinished &&
                                !isMatchConfirmed &&
                                isOrganizer &&
                                player.participantId &&
                                ["pending", "invited"].includes(
                                  player.status,
                                ) ? (
                                  <div className="match-player-slot__actions">
                                    {player.status === "pending" ? (
                                      <button
                                        type="button"
                                        className="match-player-slot__action match-player-slot__action--confirm"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void handleReviewParticipant(
                                            player.participantId,
                                            "confirmed",
                                          );
                                        }}
                                        disabled={
                                          participantActionId ===
                                          player.participantId
                                        }
                                        aria-label={
                                          texts.detail.approveParticipant
                                        }
                                        title={texts.detail.approveParticipant}
                                      >
                                        <span
                                          className="material-symbols-outlined"
                                          aria-hidden="true"
                                        >
                                          check
                                        </span>
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="match-player-slot__action match-player-slot__action--decline"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        void handleReviewParticipant(
                                          player.participantId,
                                          "declined",
                                        );
                                      }}
                                      disabled={
                                        participantActionId ===
                                        player.participantId
                                      }
                                      aria-label={
                                        texts.detail.declineParticipant
                                      }
                                      title={texts.detail.declineParticipant}
                                    >
                                      <span
                                        className="material-symbols-outlined"
                                        aria-hidden="true"
                                      >
                                        close
                                      </span>
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </>
                          ) : (
                            <>
                              <span
                                className="match-player-slot__empty-icon material-symbols-outlined"
                                aria-hidden="true"
                              >
                                person_add
                              </span>
                              <div>
                                <strong>{texts.card.emptySlot}</strong>
                              </div>
                            </>
                          )}
                        </article>
                      ))}
                    </div>
                  </div>
                </section>

                {reserveCapacity > 0 ? (
                  <section className="match-detail-panel">
                    <h3 className="match-detail-panel__title">
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        group_add
                      </span>
                      {texts.detail.reservePlayersTitle}
                    </h3>
                    <p className="match-detail-panel__description">
                      {texts.detail.reservePlayersDescription}
                    </p>
                    {reserveSlots.length > 0 ? (
                      <div className="match-reserve-grid">
                        {reserveSlots.map(({ id, player }) => (
                          <article
                            key={id}
                            className={`match-player-slot match-player-slot--reserve${
                              player ? "" : " match-player-slot--empty"
                            }${
                              player && !player.isPlatformUser
                                ? " match-player-slot--external"
                                : ""
                            }${
                              !isMatchCanceled &&
                              !isMatchFinished &&
                              !isMatchConfirmed &&
                              !player &&
                              isOrganizer
                                ? " match-player-slot--clickable"
                                : ""
                            }${
                              !isMatchCanceled && player?.userId
                                ? " match-player-slot--profile-link"
                                : ""
                            }`}
                            onClick={
                              !isMatchCanceled &&
                              !isMatchFinished &&
                              !isMatchConfirmed &&
                              !player &&
                              isOrganizer
                                ? () => setAddParticipantOpen(true)
                                : !isMatchCanceled && player?.userId
                                  ? () => {
                                      window.location.hash = `#/jogadores/${player.userId}`;
                                    }
                                  : undefined
                            }
                            onKeyDown={
                              !isMatchCanceled &&
                              !isMatchFinished &&
                              !isMatchConfirmed &&
                              !player &&
                              isOrganizer
                                ? (event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();
                                      setAddParticipantOpen(true);
                                    }
                                  }
                                : !isMatchCanceled && player?.userId
                                  ? (event) => {
                                      if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                      ) {
                                        event.preventDefault();
                                        window.location.hash = `#/jogadores/${player.userId}`;
                                      }
                                    }
                                  : undefined
                            }
                            role={
                              !isMatchCanceled &&
                              !isMatchFinished &&
                              !isMatchConfirmed &&
                              !player &&
                              isOrganizer
                                ? "button"
                                : !isMatchCanceled && player?.userId
                                  ? "link"
                                  : undefined
                            }
                            tabIndex={
                              (!isMatchCanceled &&
                                !isMatchFinished &&
                                !isMatchConfirmed &&
                                !player &&
                                isOrganizer) ||
                              (!isMatchCanceled && player?.userId)
                                ? 0
                                : undefined
                            }
                          >
                            {player ? (
                              <>
                                <UserAvatar
                                  name={player.name}
                                  avatarUrl={player.avatarUrl || undefined}
                                  sex={player.sex || undefined}
                                  size={46}
                                />
                                <div>
                                  <div className="match-player-slot__identity">
                                    <strong>{player.name}</strong>
                                    <MatchPlayerStatusIcon
                                      status={player.status}
                                      label={player.meta}
                                    />
                                  </div>
                                  {!player.isPlatformUser ? (
                                    <span className="match-player-slot__platform-badge">
                                      {texts.detail.participantNotRegistered}
                                    </span>
                                  ) : null}
                                  {!isMatchCanceled &&
                                  !isMatchFinished &&
                                  !isMatchConfirmed &&
                                  isOrganizer &&
                                  player.participantId &&
                                  ["pending", "invited"].includes(
                                    player.status,
                                  ) ? (
                                    <div className="match-player-slot__actions">
                                      {player.status === "pending" ? (
                                        <button
                                          type="button"
                                          className="match-player-slot__action match-player-slot__action--confirm"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            void handleReviewParticipant(
                                              player.participantId,
                                              "confirmed",
                                            );
                                          }}
                                          disabled={
                                            participantActionId ===
                                            player.participantId
                                          }
                                          aria-label={
                                            texts.detail.approveParticipant
                                          }
                                          title={
                                            texts.detail.approveParticipant
                                          }
                                        >
                                          <span
                                            className="material-symbols-outlined"
                                            aria-hidden="true"
                                          >
                                            check
                                          </span>
                                        </button>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="match-player-slot__action match-player-slot__action--decline"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          void handleReviewParticipant(
                                            player.participantId,
                                            "declined",
                                          );
                                        }}
                                        disabled={
                                          participantActionId ===
                                          player.participantId
                                        }
                                        aria-label={
                                          texts.detail.declineParticipant
                                        }
                                        title={texts.detail.declineParticipant}
                                      >
                                        <span
                                          className="material-symbols-outlined"
                                          aria-hidden="true"
                                        >
                                          close
                                        </span>
                                      </button>
                                    </div>
                                  ) : null}
                                </div>
                              </>
                            ) : (
                              <>
                                <span
                                  className="match-player-slot__empty-icon material-symbols-outlined"
                                  aria-hidden="true"
                                >
                                  person_add
                                </span>
                                <div>
                                  <strong>{texts.card.emptySlot}</strong>
                                </div>
                              </>
                            )}
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="match-detail-panel__empty">
                        {texts.detail.noReserveSlots}
                      </p>
                    )}
                  </section>
                ) : null}

                <section className="match-detail-panel">
                  <h3 className="match-detail-panel__title">
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      info
                    </span>
                    {texts.detail.details}
                  </h3>
                  <div className="match-detail-about-grid">
                    <article>
                      <div className="info">
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          bar_chart
                        </span>
                        <div>
                          <span>{texts.detail.categories}</span>
                          <div className="match-detail-category-box">
                            <div className="match-category-badges">
                              {uniqueCategories.map((category) => (
                                <span
                                  key={`match-detail-category-${match.id}-${category}`}
                                  className={`match-card__chip match-card__chip--category match-category-badge match-category-badge--${resolveOpenMatchCategoryClass(category)}`}
                                >
                                  {formatOpenMatchCategory(category)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                    <article>
                      <div className="info">
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          person
                        </span>
                        <div>
                          <span>{texts.card.gender}</span>
                          <div>
                            <strong>
                              {formatOpenMatchGenderPreference(
                                match.genderPreference,
                              )}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </article>
                    <article>
                      <div className="info">
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          sports_baseball
                        </span>
                        <div>
                          <span>{texts.card.side}</span>
                          <div>
                            <strong>
                              {formatOpenMatchSideRequirement(match)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </article>
                    <article>
                      <div className="info">
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          schedule
                        </span>
                        <div>
                          <span>{texts.detail.duration}</span>
                          <div>
                            <strong>
                              {resolveOpenMatchDurationMinutes(match)} min
                            </strong>
                          </div>
                        </div>
                      </div>
                    </article>
                    <article>
                      <div className="info">
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          credit_card
                        </span>
                        <div>
                          <span>{texts.detail.payment}</span>
                          <div>
                            <strong>
                              {formatOpenMatchPaymentMode(match.paymentMode)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                  {detailsText ? (
                    <p className="match-detail-card__details-copy">
                      {detailsText}
                    </p>
                  ) : null}
                </section>

                <section className="match-detail-club-card">
                  {clubLogoUrl ? (
                    <img
                      className="match-detail-club-card__logo"
                      src={clubLogoUrl}
                      alt={match.clubName}
                    />
                  ) : (
                    <span
                      className="match-detail-club-card__icon material-symbols-outlined"
                      aria-hidden="true"
                    >
                      location_on
                    </span>
                  )}
                  <div>
                    <strong>{match.clubName}</strong>
                    <span>
                      {match.address || match.city}
                      {match.state ? ` • ${match.state}` : ""}
                    </span>
                  </div>
                  {!isMatchCanceled && clubHref ? (
                    <a href={clubHref}>
                      {texts.detail.viewClub}
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        chevron_right
                      </span>
                    </a>
                  ) : null}
                </section>

                <section className="match-detail-panel match-detail-chat">
                  <div className="match-detail-chat__header">
                    <h3 className="match-detail-panel__title">
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        forum
                      </span>
                      {texts.detail.messageBoardTitle}
                      {Number(chatState?.unreadCount || 0) > 0 ? (
                        <span className="match-detail-chat__unread">
                          {chatState?.unreadCount}
                        </span>
                      ) : null}
                    </h3>
                    {canAccessChat ? (
                      <button
                        type="button"
                        className="match-detail-chat__read-button"
                        onClick={handleMarkChatRead}
                      >
                        <span
                          className="material-symbols-outlined"
                          aria-hidden="true"
                        >
                          done_all
                        </span>
                      </button>
                    ) : null}
                  </div>

                  {!canAccessChat ? (
                    <p className="match-detail-panel__empty">
                      {texts.detail.messageNoAccess}
                    </p>
                  ) : (
                    <>
                      {chatReadOnlyMessage ? (
                        <div className="match-detail-chat__notice" role="status">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            lock
                          </span>
                          {chatReadOnlyMessage}
                        </div>
                      ) : null}
                      {chatLoading ? (
                        <p className="match-detail-panel__empty">
                          {texts.detail.messageLoading}
                        </p>
                      ) : chatMessages.length ? (
                        <div className="match-detail-chat__messages">
                          {chatMessages.map((message) => {
                            const isSystem = message.messageType !== "text";
                            const canRemoveMessage = Boolean(
                              !message.deletedAt &&
                                !isSystem &&
                                canSendChat &&
                                (isOrganizer ||
                                  Number(message.userId || 0) ===
                                    Number(user?.id || 0) ||
                                  String((user as any)?.role || "") === "admin"),
                            );
                            const createdAt = new Date(message.createdAt || "");
                            const timeLabel = Number.isNaN(createdAt.getTime())
                              ? ""
                              : new Intl.DateTimeFormat("pt-BR", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }).format(createdAt);
                            return (
                              <article
                                key={message.id}
                                className={`match-detail-chat__message${
                                  isSystem
                                    ? " match-detail-chat__message--system"
                                    : ""
                                }`}
                              >
                                {!isSystem ? (
                                  <UserAvatar
                                    name={message.userName}
                                    avatarUrl={message.userAvatarUrl}
                                    size={36}
                                    fallback="initials"
                                  />
                                ) : (
                                  <span
                                    className="match-detail-chat__system-icon material-symbols-outlined"
                                    aria-hidden="true"
                                  >
                                    info
                                  </span>
                                )}
                                <div className="match-detail-chat__bubble">
                                  <header>
                                    <strong>
                                      {isSystem
                                        ? texts.detail.messageSystemAuthor
                                        : message.userName}
                                    </strong>
                                    {Number(message.userId || 0) ===
                                    Number(match.organizer.id) ? (
                                      <span>
                                        {texts.detail.organizerBadge}
                                      </span>
                                    ) : null}
                                    {timeLabel ? <time>{timeLabel}</time> : null}
                                  </header>
                                  <p>
                                    {message.deletedAt
                                      ? texts.detail.messageRemoved
                                      : message.message}
                                  </p>
                                </div>
                                {canRemoveMessage ? (
                                  <button
                                    type="button"
                                    className="match-detail-chat__remove-button"
                                    onClick={() =>
                                      handleRemoveChatMessage(message.id)
                                    }
                                    disabled={removingMessageId === message.id}
                                    aria-label={texts.detail.messageRemove}
                                  >
                                    <span
                                      className="material-symbols-outlined"
                                      aria-hidden="true"
                                    >
                                      delete
                                    </span>
                                  </button>
                                ) : null}
                              </article>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="match-detail-panel__empty">
                          {texts.detail.noMessages}
                        </p>
                      )}
                      {chatError ? (
                        <p className="match-detail-chat__error" role="alert">
                          {chatError}
                        </p>
                      ) : null}
                      <form
                        className="match-detail-chat__form"
                        onSubmit={handleSendChatMessage}
                      >
                        <label>
                          <span>{texts.detail.messageLabel}</span>
                          <textarea
                            value={chatDraft}
                            onChange={(event) =>
                              setChatDraft(
                                event.target.value.slice(
                                  0,
                                  MATCH_CHAT_MESSAGE_LIMIT,
                                ),
                              )
                            }
                            placeholder={texts.detail.messagePlaceholder}
                            disabled={!canSendChat || chatSending}
                            rows={3}
                          />
                        </label>
                        <div className="match-detail-chat__form-footer">
                          <span>
                            {texts.detail.messageCharacters
                              .replace("{count}", String(chatDraft.length))
                              .replace(
                                "{limit}",
                                String(MATCH_CHAT_MESSAGE_LIMIT),
                              )}
                          </span>
                          <button
                            className="match-detail-chat__send-button"
                            type="submit"
                            disabled={
                              !canSendChat || chatSending || !chatDraft.trim()
                            }
                          >
                            <span
                              className="material-symbols-outlined"
                              aria-hidden="true"
                            >
                              send
                            </span>
                            {chatSending
                              ? texts.detail.messageSending
                              : texts.detail.messageSend}
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </section>
              </div>
            </aside>
          </div>
        </section>
      </div>

      {toastMessage ? (
        <Toast message={toastMessage} onClose={() => setToastMessage("")} />
      ) : null}
      <MatchParticipantModal
        open={!isMatchCanceled && !isMatchFinished && addParticipantOpen}
        match={match}
        saving={addParticipantSaving}
        onSubmit={handleAddParticipant}
        onClose={() => setAddParticipantOpen(false)}
      />
      <MatchCreatePanel
        open={!isMatchCanceled && !isMatchFinished && editingMatchOpen}
        clubs={editClubs}
        loadingClubs={editClubsLoading}
        mode="edit"
        initialMatch={match}
        defaultOrganizerName={user?.name || ""}
        onSubmit={handleUpdateMatch}
        onCancelMatch={handleCancelMatch}
        onAddParticipant={handleAddParticipant}
        onReviewParticipant={handleReviewParticipant}
        reviewingParticipantId={participantActionId}
        onClose={() => setEditingMatchOpen(false)}
      />
    </AppLayout>
  );
}
